import { AbstractSamwinHeadsetManufacturerService } from './headset-manufacturer-service.abstract';
import { Store } from '@ngxs/store';
import { ChangesToHeadsetState } from './changes-to-headset-state.enum';
import { Injectable } from '@angular/core';
import { secomCCWebLibObj, WSMsgFormat } from './SDKs/SecomCCModule.js';
import { HeadsetManufacturerType } from './headset-manufacturer-type';

enum SennheiserMessageTypes {
   NOTIFICATION = 'Notification',
   ACKNOWLEDGEMENT = 'Acknowledgement',
}

enum SennheiserNotifications {
   SOCKET_CONNECTED = 'SocketConnected',
   HEADSET_CONNECTED = 'HeadsetConnected',
   HEADSET_DISCONNECTED = 'HeadsetDisconnected',
   HEADSET_MUTED = 'MuteSoftphone',
   HEADSET_UNMUTED = 'UnmuteSoftphone',
}

@Injectable({
   providedIn: 'root',
})
export class SennheiserService extends AbstractSamwinHeadsetManufacturerService {
   private lastCallWasOutgoing: boolean;
   private lastCallGotAccepted: boolean;
   private isMuted: boolean;
   private CALL_ID = 99999;
   private isInitialized: boolean;

   constructor(store: Store) {
      super(store, HeadsetManufacturerType.SENNHEISER);
   }

   protected initializeSdk(): void {
      try {
         console.info('Initializing Sennheiser headset');
         secomCCWebLibObj.init();
         this.isInitialized = true;
         secomCCWebLibObj.onNewEvent((parsedMessage) => {
            // handle notifications
            if (parsedMessage.type === SennheiserMessageTypes.NOTIFICATION) {
               console.debug(`Sennheiser notification message received: '${parsedMessage.message}'`);
               switch (parsedMessage.message) {
                  case SennheiserNotifications.SOCKET_CONNECTED:
                     secomCCWebLibObj.sendToCCWebLib(WSMsgFormat.WSMsgEvent.EstablishConnection[0].message.Event);
                     break;
                  case SennheiserNotifications.HEADSET_CONNECTED:
                     const deviceData = JSON.parse(parsedMessage.data);
                     console.debug(`Sennheiser headset device connected: '${deviceData.HeadsetName}'`);
                     this.attachedHeadset$.next([
                        { manufacturer: HeadsetManufacturerType.SENNHEISER, id: deviceData.HeadsetPath, name: deviceData.HeadsetName },
                     ]);
                     this.bringDeviceUpToState();
                     break;
                  case SennheiserNotifications.HEADSET_DISCONNECTED:
                     const disconnectedDevice = JSON.parse(parsedMessage.data);
                     console.debug(`Sennheiser headset device disconnected: '${disconnectedDevice.HeadsetName}'`);
                     this.lastCallGotAccepted = undefined;
                     this.lastCallWasOutgoing = undefined;
                     this.attachedHeadset$.next([]);
                     break;
                  case SennheiserNotifications.HEADSET_MUTED:
                     this.toggleMute();
                     break;
                  case SennheiserNotifications.HEADSET_UNMUTED:
                     this.toggleMute();
                     break;
                  case WSMsgFormat.WSMsgEvent.InCallAccepted[0].message.Event:
                     this.acceptIncomingCall();
                     break;
                  case WSMsgFormat.WSMsgEvent.CallEnded[0].message.Event:
                     this.endCall();
                     break;
               }
            } else if (parsedMessage.type === SennheiserMessageTypes.ACKNOWLEDGEMENT) {
               // handle acknowledgements
               console.debug(`Sennheiser acknowledgement message received: '${parsedMessage.message}'`);
               switch (parsedMessage.message) {
                  case WSMsgFormat.WSMsgEvent.EstablishConnection[0].message.Event:
                     secomCCWebLibObj.sendToCCWebLib(WSMsgFormat.WSMsgEvent.SPLoggedIn[0].message.Event);
                     break;
               }
            }
         });
      } catch (err) {
         console.error(`Failed to init Sennheiser headset client, reason:`, err);
      }
   }

   protected terminateSdk(): void {
      if (!this.isInitialized) {
         return;
      }
      this.isInitialized = false;
      secomCCWebLibObj?.close();
   }

   public changeHeadsetState(change: ChangesToHeadsetState, callId?: number): void {
      console.debug(
         `Changing Sennheiser headset state to state ${change}. Current state: lastCallGotAccepted - ${this.lastCallGotAccepted}, lastCallWasOutgoing - ${this.lastCallWasOutgoing}`
      );
      switch (change) {
         case ChangesToHeadsetState.MuteHeadset:
            secomCCWebLibObj.sendToCCWebLib(WSMsgFormat.WSMsgEvent.MuteHeadset[0].message.Event, this.CALL_ID);
            this.isMuted = true;
            break;
         case ChangesToHeadsetState.UnmuteHeadset:
            if (this.isMuted) {
               secomCCWebLibObj.sendToCCWebLib(WSMsgFormat.WSMsgEvent.UnmuteHeadset[0].message.Event, this.CALL_ID);
               this.isMuted = false;
            }
            break;
         case ChangesToHeadsetState.OnHook:
            // ensure we don't try to terminate call during init
            if (this.lastCallWasOutgoing !== undefined) {
               // terminating call with Sennheiser headset differentiates between call types
               if (this.lastCallGotAccepted) {
                  this.lastCallGotAccepted = false;
                  secomCCWebLibObj.sendToCCWebLib(WSMsgFormat.WSMsgEvent.CallEnded[0].message.Event, this.CALL_ID);
               } else if (this.lastCallWasOutgoing) {
                  secomCCWebLibObj.sendToCCWebLib(WSMsgFormat.WSMsgEvent.OutCallRejected[0].message.Event, this.CALL_ID);
               } else {
                  secomCCWebLibObj.sendToCCWebLib(WSMsgFormat.WSMsgEvent.InCallRejected[0].message.Event, this.CALL_ID);
               }
            }
            break;
         case ChangesToHeadsetState.OffHook:
            if (this.lastCallWasOutgoing) {
               secomCCWebLibObj.sendToCCWebLib(WSMsgFormat.WSMsgEvent.OutCallAccepted[0].message.Event, this.CALL_ID);
            } else {
               secomCCWebLibObj.sendToCCWebLib(WSMsgFormat.WSMsgEvent.InCallAccepted[0].message.Event, this.CALL_ID);
            }
            this.lastCallGotAccepted = true;
            break;
         case ChangesToHeadsetState.IncomingCall:
            this.lastCallWasOutgoing = false;
            secomCCWebLibObj.sendToCCWebLib(WSMsgFormat.WSMsgEvent.IncomingCall[0].message.Event, this.CALL_ID);
            break;
         case ChangesToHeadsetState.OutgoingCall:
            this.lastCallWasOutgoing = true;
            secomCCWebLibObj.sendToCCWebLib(WSMsgFormat.WSMsgEvent.OutgoingCall[0].message.Event, this.CALL_ID);
            break;
         case ChangesToHeadsetState.Hold:
            if (this.lastCallWasOutgoing !== undefined) {
               secomCCWebLibObj.sendToCCWebLib(WSMsgFormat.WSMsgEvent.CallHold[0].message.Event, this.CALL_ID);
            }
            break;
         case ChangesToHeadsetState.Resume:
            if (this.lastCallWasOutgoing !== undefined) {
               secomCCWebLibObj.sendToCCWebLib(WSMsgFormat.WSMsgEvent.HeldCallResumed[0].message.Event, this.CALL_ID);
            }
            break;
      }
   }
}
